import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { providerFullDisplayName } from "@pontimed/types";
import { apiFetch } from "@pontimed/api-client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import {
  Calendar,
  CalendarClock,
  CalendarX,
  Clock,
  History,
  Inbox,
  Plus,
  Search,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { PatientRequestsPanel } from "@/components/account/booking-requests/patient-requests-panel";
import {
  AppointmentCard,
  type AppointmentCardData,
} from "@/components/appointments/appointment-card";
import { AppointmentDetail } from "@/components/appointments/appointment-detail";
import { CancelPolicyModal } from "@/components/appointments/cancel-policy-modal";
import { PendingReviewsBanner } from "@/components/appointments/pending-reviews-banner";
import { WaitlistTab } from "@/components/appointments/waitlist-tab";
import { RescheduleSheet } from "@/components/booking/reschedule-sheet";
import { PageHeader } from "@/components/layout/page-header";
import { ReviewModal } from "@/components/reviews/review-modal";
import { Button } from "@/components/ui/button";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { showToast } from "@/lib/ui/toast";

type Tab = "upcoming" | "past" | "cancelled" | "waitlist" | "requests";
type ListTab = Exclude<Tab, "waitlist" | "requests">;

const VALID_TABS: readonly Tab[] = [
  "upcoming",
  "past",
  "cancelled",
  "waitlist",
  "requests",
] as const;

interface ReviewTarget {
  appointmentId: string;
  providerName: string;
  providerPhoto?: string | null;
  visitDate: string;
}

interface AppointmentsSearch {
  tab: Tab;
  review?: string;
  /** Opens the reschedule sheet for this appointment ("Pick another time" on the confirm page). */
  reschedule?: string;
}

/** What the reschedule sheet needs — a list card, or the appointment fetched for a deep link. */
type RescheduleTarget = Pick<AppointmentCardData, "id" | "startsAt" | "duration" | "status"> & {
  provider: { id: string };
  facility: { id: string } | null;
};

export const Route = createFileRoute("/_authenticated/account/user/appointments")({
  validateSearch: (search: Record<string, unknown>): AppointmentsSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    const review = typeof search.review === "string" ? search.review : undefined;
    const reschedule = typeof search.reschedule === "string" ? search.reschedule : undefined;
    return {
      tab: VALID_TABS.includes(raw as Tab) ? (raw as Tab) : "upcoming",
      review,
      reschedule,
    };
  },
  component: AppointmentsPage,
});

function AppointmentsPage() {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const { tab, review: reviewDeepLinkId, reschedule: rescheduleDeepLinkId } = Route.useSearch();
  const navigate = useNavigate();
  const [detailId, setDetailId] = useState<string | null>(null);
  /** The detail sheet's footer — AppointmentDetail portals its actions into it (P21). */
  const [detailFooterEl, setDetailFooterEl] = useState<HTMLDivElement | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);
  const [bannerRefreshKey, setBannerRefreshKey] = useState(0);
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  const refreshFnsRef = useRef<Map<ListTab, () => void>>(new Map());
  const registerRefresh = useCallback((id: ListTab, fn: () => void) => {
    refreshFnsRef.current.set(id, fn);
    return () => {
      if (refreshFnsRef.current.get(id) === fn) {
        refreshFnsRef.current.delete(id);
      }
    };
  }, []);
  const refreshActivePanel = useCallback(() => {
    if (tab === "waitlist" || tab === "requests") return;
    refreshFnsRef.current.get(tab)?.();
  }, [tab]);

  const aptCacheRef = useRef<Map<string, AppointmentCardData>>(new Map());
  // Bumped on every report so the `?review=<id>` effect below re-runs once the
  // Past panel has loaded: the cache is a ref, and filling it re-renders nothing.
  const [aptCacheVersion, setAptCacheVersion] = useState(0);
  const reportAppointments = useCallback((apts: AppointmentCardData[]) => {
    apts.forEach((a) => aptCacheRef.current.set(a.id, a));
    setAptCacheVersion((v) => v + 1);
  }, []);

  const [rescheduleTarget, setRescheduleTarget] = useState<RescheduleTarget | null>(null);

  // `?reschedule=<id>`: the confirm page's "Pick another time" after the
  // clinic moved the appointment. Fetched by id — it may belong to a dependent
  // or sit beyond the first page of the list. The param is dropped either way
  // so closing the sheet does not reopen it.
  useEffect(() => {
    if (!rescheduleDeepLinkId) return;
    const controller = new AbortController();
    apiFetch
      .get<RescheduleTarget>(`/api/appointments/${rescheduleDeepLinkId}`, {
        signal: controller.signal,
      })
      .then((apt) => {
        if (apt.status === "confirmed" && new Date(apt.startsAt).getTime() > Date.now()) {
          setRescheduleTarget(apt);
        } else {
          showToast(t`This appointment can no longer be moved.`, "error");
        }
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        showToast(t`We couldn't open this appointment. Try again.`, "error");
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        void navigate({
          to: "/account/user/appointments",
          search: (prev) => ({ ...prev, tab, reschedule: undefined }),
          replace: true,
        });
      });
    return () => controller.abort();
  }, [rescheduleDeepLinkId, navigate, t, tab]);

  const [upcomingTotal, setUpcomingTotal] = useState(0);
  const handleUpcomingTotalChange = useCallback((n: number) => {
    setUpcomingTotal(n);
  }, []);

  const setTab = useCallback(
    (id: Tab) =>
      navigate({
        to: "/account/user/appointments",
        search: (prev) => ({ ...prev, tab: id }),
      }),
    [navigate],
  );

  // The cancel dialog shows the policy — and, paid online, the exact refund.
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const handleCancel = useCallback((id: string) => setCancelTarget(id), []);

  const handleReview = useCallback(
    (apt: AppointmentCardData) => {
      const providerName = providerFullDisplayName(apt.provider, locale);
      const visitDate = new Date(apt.startsAt).toLocaleDateString(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      setReviewTarget({
        appointmentId: apt.id,
        providerName,
        providerPhoto: apt.provider.photoUrl,
        visitDate,
      });
    },
    [locale],
  );

  const handleReviewSubmitted = () => {
    setReviewTarget(null);
    refreshActivePanel();
    setBannerRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    if (!reviewDeepLinkId || reviewTarget) return;
    const apt = aptCacheRef.current.get(reviewDeepLinkId);
    if (apt) handleReview(apt);
  }, [reviewDeepLinkId, reviewTarget, handleReview, aptCacheVersion]);

  const openReviewById = useCallback(
    (id: string) => {
      const apt = aptCacheRef.current.get(id);
      if (apt) handleReview(apt);
    },
    [handleReview],
  );

  const handleDetail = (id: string) => {
    setDetailId(id);
  };

  // Every tab carries an icon so the labels line up on mobile, where TabBar
  // stacks icon over label.
  const tabs: TabItem<Tab>[] = [
    { id: "upcoming", label: t`Upcoming`, icon: <CalendarClock className="size-4" /> },
    { id: "past", label: t`Past`, icon: <History className="size-4" /> },
    { id: "cancelled", label: t`Cancelled`, icon: <CalendarX className="size-4" /> },
    { id: "waitlist", label: t`Waiting list`, icon: <Clock className="size-4" /> },
    { id: "requests", label: t`Requests`, icon: <Inbox className="size-4" /> },
  ];
  const tabsWithBadges = tabs.map((tabItem) =>
    tabItem.id === "upcoming" && upcomingTotal > 0 && tab === "upcoming"
      ? {
          ...tabItem,
          trailing: (
            <span className="bg-primary text-primary-foreground inline-flex size-5 items-center justify-center rounded-full text-[0.625rem] font-bold">
              {upcomingTotal}
            </span>
          ),
        }
      : tabItem,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        title={<Trans>Appointments</Trans>}
        description={<Trans>Manage your upcoming, past and cancelled visits.</Trans>}
        actions={
          <Button onClick={() => navigate({ to: "/find" })} size="touch">
            <Plus className="size-4" aria-hidden />
            <Trans>New appointment</Trans>
          </Button>
        }
      />

      <PendingReviewsBanner onOpenReview={setReviewTarget} refreshKey={bannerRefreshKey} />

      <TabBar
        tabs={tabsWithBadges}
        activeTab={tab}
        onTabChange={setTab}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
        className="mt-6"
      />

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={tab}
        tabIds={tabs.map((tabItem) => tabItem.id)}
        onTabChange={setTab}
        className="mt-4"
        renderPanel={(id) => {
          if (id === "waitlist")
            return (
              <div className="space-y-6">
                <WaitlistTab />
                <AccountSecurityBanner />
              </div>
            );
          if (id === "requests")
            return (
              <div className="space-y-6">
                <PatientRequestsPanel />
                <AccountSecurityBanner />
              </div>
            );
          return (
            <div className="space-y-6">
              <AppointmentsListPanel
                tab={id}
                onCancel={handleCancel}
                onReview={handleReview}
                onReschedule={setRescheduleTarget}
                onDetail={handleDetail}
                onUpcomingTotalChange={id === "upcoming" ? handleUpcomingTotalChange : undefined}
                registerRefresh={registerRefresh}
                reportAppointments={reportAppointments}
              />
              <AccountSecurityBanner />
            </div>
          );
        }}
      />

      <ReviewModal
        open={!!reviewTarget}
        appointmentId={reviewTarget?.appointmentId ?? null}
        providerName={reviewTarget?.providerName ?? ""}
        providerPhoto={reviewTarget?.providerPhoto}
        visitDate={reviewTarget?.visitDate ?? ""}
        onClose={() => setReviewTarget(null)}
        onSubmitted={handleReviewSubmitted}
      />

      {/* Patient-side reschedule wizard. Provider-side reschedule lives in a
          separate `RescheduleModal` on `account/provider/appointments.tsx`. */}
      {rescheduleTarget && (
        <RescheduleSheet
          open
          appointmentId={rescheduleTarget.id}
          providerId={rescheduleTarget.provider.id}
          facilityId={rescheduleTarget.facility?.id ?? null}
          currentDate={rescheduleTarget.startsAt}
          currentTime={new Date(rescheduleTarget.startsAt).toLocaleTimeString(locale, {
            hour: "2-digit",
            minute: "2-digit",
          })}
          duration={rescheduleTarget.duration}
          onClose={() => setRescheduleTarget(null)}
          onRescheduled={() => {
            setRescheduleTarget(null);
            refreshActivePanel();
          }}
        />
      )}

      {cancelTarget && (
        <CancelPolicyModal
          appointmentId={cancelTarget}
          isOpen
          onCancelled={() => {
            setCancelTarget(null);
            refreshActivePanel();
          }}
          onClose={() => setCancelTarget(null)}
        />
      )}

      {/* Appointment Detail bottom sheet */}
      <ResponsiveSheet
        open={Boolean(detailId)}
        onClose={() => {
          setDetailId(null);
          refreshActivePanel();
        }}
        title={<Trans>Appointment details</Trans>}
        closeLabel={t`Close`}
        width="lg"
        bodyClassName="p-0"
        footer={
          detailId ? <div ref={setDetailFooterEl} className="flex w-full gap-2" /> : undefined
        }
      >
        {detailId && (
          <AppointmentDetail
            appointmentId={detailId}
            footerSlot={detailFooterEl}
            onClose={() => {
              setDetailId(null);
              refreshActivePanel();
            }}
            onCancel={() => refreshActivePanel()}
            onRebook={() => {
              setDetailId(null);
              navigate({ to: "/find" });
            }}
            onReview={() => {
              if (detailId) {
                openReviewById(detailId);
                setDetailId(null);
              }
            }}
          />
        )}
      </ResponsiveSheet>
    </div>
  );
}

interface AppointmentsListPanelProps {
  tab: ListTab;
  onCancel: (id: string) => void;
  onReview: (apt: AppointmentCardData) => void;
  onReschedule: (apt: AppointmentCardData) => void;
  onDetail: (id: string) => void;
  onUpcomingTotalChange?: (n: number) => void;
  registerRefresh: (id: ListTab, fn: () => void) => () => void;
  reportAppointments: (apts: AppointmentCardData[]) => void;
}

interface AppointmentsListResponse {
  appointments: AppointmentCardData[];
  total: number;
  page: number;
  totalPages: number;
}

function AppointmentsListPanel({
  tab,
  onCancel,
  onReview,
  onReschedule,
  onDetail,
  onUpcomingTotalChange,
  registerRefresh,
  reportAppointments,
}: AppointmentsListPanelProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<AppointmentCardData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  /** A failed load is not "no appointments" (P16). */
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch.get<AppointmentsListResponse>(
        `/api/account/appointments?tab=${tab}&page=${page}&limit=20`,
      );
      const apts: AppointmentCardData[] = data.appointments || [];
      setItems(apts);
      setTotal(data.total || 0);
      setLoadFailed(false);
      reportAppointments(apts);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [tab, page, reportAppointments]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => registerRefresh(tab, load), [tab, load, registerRefresh]);

  useEffect(() => {
    onUpcomingTotalChange?.(total);
  }, [total, onUpcomingTotalChange]);

  const handleCardReview = useCallback(
    (id: string) => {
      const apt = items.find((a) => a.id === id);
      if (apt) onReview(apt);
    },
    [items, onReview],
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-muted h-24 animate-pulse rounded-xl" />
        ))}
      </div>
    );
  }

  if (loadFailed && items.length === 0) {
    return (
      <LoadErrorState
        title={<Trans>We couldn't load your appointments.</Trans>}
        description={
          <Trans>Your appointments are safe. Check your connection and try again.</Trans>
        }
        onRetry={() => void load()}
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="border-border bg-card rounded-xl border p-5">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="bg-muted flex size-16 items-center justify-center rounded-full">
            <Calendar className="text-muted-foreground size-8" />
          </div>
          <h3 className="text-foreground mt-4 text-sm font-semibold">
            <Trans>No appointments</Trans>
          </h3>
          <p className="text-muted-foreground mt-1 max-w-xs text-center text-sm">
            {tab === "upcoming" ? (
              <Trans>You don't have any upcoming visits scheduled.</Trans>
            ) : tab === "past" ? (
              <Trans>No past appointments yet.</Trans>
            ) : (
              <Trans>No cancelled appointments.</Trans>
            )}
          </p>
          {tab === "upcoming" && (
            <Button onClick={() => navigate({ to: "/find" })} className="mt-4" size="touch">
              <Search className="size-4" aria-hidden /> <Trans>Find a provider</Trans>
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {items.map((apt, i) => (
            <AppointmentCard
              key={apt.id}
              appointment={apt}
              tab={tab}
              index={i}
              onCancel={onCancel}
              onReview={handleCardReview}
              onReschedule={onReschedule}
              onDetail={onDetail}
            />
          ))}
        </AnimatePresence>
      </div>

      {total > 20 && (
        <div className="mt-4 flex justify-center gap-2">
          <Button
            size="touch"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <Trans>Previous</Trans>
          </Button>
          <span className="text-muted-foreground flex items-center text-xs">
            {page} / {Math.ceil(total / 20)}
          </span>
          <Button
            size="touch"
            variant="outline"
            disabled={page >= Math.ceil(total / 20)}
            onClick={() => setPage((p) => p + 1)}
          >
            <Trans>Next</Trans>
          </Button>
        </div>
      )}
    </>
  );
}
